import type { SendingMethod, ServiceType, ShipmentStatusGroup } from './common';
import type { Order } from './orders';
import type { PackageTemplate, ParcelLimits } from './package-templates';
import type { Shipment } from './shipments';

/** Pseudo-status accepted by the merged listing for orders without shipments. */
export const ORDER_SHIPMENT_STATUS_NEW = 'NEW' as const;

/**
 * Status filter values: a colour-coding group (the latest carrier event code's
 * bucket) or `NEW` for orders that have no shipment yet.
 */
export type OrderStatusFilter = ShipmentStatusGroup | typeof ORDER_SHIPMENT_STATUS_NEW;

export type OrderWithShipments = {
  order: Order;
  shipments: Shipment[];
};

export type OrderShipmentFilters = {
  status?: OrderStatusFilter | null;
  /** Optional drill-down to a single tracking event code, e.g. `EOL.1001`. */
  event_code?: string | null;
  service_type?: ServiceType | null;
  order_number?: string | null;
  email?: string | null;
  phone?: string | null;
  tracking_number?: string | null;
  reference?: string | null;
  tag?: string | null;
  missing_point?: 1 | null;
  /** Order creation date range; `date_to` is inclusive of the whole day. */
  date_from?: string | null;
  date_to?: string | null;
  /** Shipment creation date range; `shipment_date_to` is inclusive of the whole day. */
  shipment_date_from?: string | null;
  shipment_date_to?: string | null;
  page?: number;
  per_page?: number;
};

export type OrderProduct = {
  id: string | null;
  title: string;
  quantity: number;
  unit_price: string | null;
  total_price: string | null;
  currency: string | null;
  image_url: string | null;
};

export type OrderBillingAddress = {
  name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  zip: string | null;
  country_code: string | null;
  phone: string | null;
};

export type OrderDetails = {
  order: Order;
  shipments: Shipment[];
  /** Null when the live Shopify lookup is unavailable or failed. */
  products: OrderProduct[] | null;
  billing_address: OrderBillingAddress | null;
  /**
   * Sending methods the merchant may dispatch this order with, keyed by
   * delivery method (the corridor cell depends on the delivery side). A
   * delivery method with an empty list is unavailable for this order; all
   * lists are empty when no valid corridor exists.
   */
  allowed_methods: Partial<Record<ServiceType, SendingMethod[]>>;
  /** Whether the order's checkout delivery method is a cash-on-delivery one (preselects the COD variant in the shipment form). */
  cash_on_delivery: boolean;
  /** Whether the order's checkout delivery method is a C2C one (preselects the C2C delivery service in the shipment form). */
  c2c: boolean;
  /**
   * Currency COD is collected in at the order's destination - may differ from
   * the order currency (e.g. PLN for PL deliveries of an EUR-selling shop).
   */
  cod_currency: string | null;
  /** Currency insurance is charged in at the order's destination (destination-resolved, like COD). */
  insurance_currency: string | null;
  /**
   * Whether InPost collects cash on delivery on the order's corridor (false
   * for PL→AT/HU/SK/CZ - the COD delivery options are disabled). Absent on a
   * pre-feature backend = available.
   */
  cod_available?: boolean;
  /**
   * Whether InPost insures parcels on the order's corridor (false for
   * PL→AT/HU/SK/CZ - the insurance input is disabled). Absent on a
   * pre-feature backend = available.
   */
  insurance_available?: boolean;
  /**
   * Predefined package sizes offered for deliveries to the order's destination
   * (empty for markets without predefined sizes - custom packages only).
   * Absent on a pre-feature backend.
   */
  package_templates?: PackageTemplate[];
  /**
   * Maximum parcel size per delivery service at the order's destination, keyed
   * by service type; null = the service enforces no limit. Absent on a
   * pre-feature backend (nothing is greyed out then).
   */
  parcel_limits?: Partial<Record<ServiceType, ParcelLimits | null>>;
  /**
   * Maximum parcel size the merchant can hand to a point in the origin
   * country, keyed by sending method (null = courier pickup / no cap). A
   * courier-delivery parcel dropped at a PUDO still has to fit the PUDO.
   * Absent on a pre-feature backend.
   */
  drop_off_limits?: Partial<Record<SendingMethod, ParcelLimits | null>>;
};
